import React, { useCallback, useMemo, useState } from "react";
import MessageInput from "./MessageInput";
import PredefinedPrompts from "../PredefinedPrompts";
import PromptModal from "../PromptModal";
import styles from "./chatwindow.module.css";
import MessageBubble from "./MessageBubble";
import LoadingSpinner from "../LoadingSpinner";
import ContentCard from "../ContentCard";
import { Agents } from "../../types/master";
import { useChatCore } from "../ChatCore";
import { v4 as uuid } from "uuid";
import { getAgentSubtitle } from "../../utils/helper";
import IntakeAgentForm from "../AgentResponse/IntakeAgentForm";
import LearningPathButton from "./LearningPathButton";

interface ChatWindowProps {
  agentId?: string;
  chatWindowColor?: string;
  currentAgent?: Agents | undefined;
  templateBlobUrl?: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  agentId,
  chatWindowColor,
  currentAgent,
  templateBlobUrl,
}) => {
  const sessionId = useMemo(() => uuid(), []);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(true);
  const [selectedTemplateFileName, setSelectedTemplateFileName] = useState<
    string | undefined
  >();

  // Use shared chat logic
  const {
    agent,
    currentChat,
    loading,
    hasMessages,
    showFullScreenSpinner,
    defaultPrompts,
    value,
    setValue,
    inputRef,
    handlePromptClick,
  } = useChatCore(sessionId, currentAgent, agentId);

  // Agent-specific title/description
  let agentTitle = `${agent?.agent_name || ""}`;
  const agentSubTitle = getAgentSubtitle(agentId);

  const onPromptClick = useCallback(
    (prompt: string, templateFileName?: string) => {
      // Store the template file name
      setSelectedTemplateFileName(templateFileName);
      // Open the modal first (except for agent ID 26)
      if (agentId !== "26") {
        setIsPromptModalOpen(true);
      }
      // Then handle the prompt click
      handlePromptClick(prompt);
    },
    [handlePromptClick, agentId],
  );

  const handleClosePromptModal = useCallback(() => {
    setIsPromptModalOpen(false);
  }, []);

  // Check if there are any agent responses in the chat
  const hasAgentResponse = currentChat.some((msg) => msg.sender === "agent");

  // Check if workflow has ended (only for HR Agent - ID 18)
  // Workflow ends when last message is email_drafting AND contains draft_emails (final step)
  const lastAgentMessage = [...currentChat]
    .reverse()
    .find((msg) => msg.sender === "agent");

  // Check if SOW workflow is completed
  let isSowCompleted = false;
  if (lastAgentMessage?.agentIdName === "sow_auto_filler") {
    try {
      const parsedMessage = JSON.parse(lastAgentMessage.message);
      const finalAnswer = parsedMessage?.finalAnswer || parsedMessage;
      isSowCompleted = finalAnswer?.status === "completed";
    } catch (e) {
      // If parsing fails, keep isSowCompleted as false
    }
  }

  const isWorkflowEnded =
    (agentId === "18" &&
      !loading &&
      lastAgentMessage?.agentIdName === "email_drafting" &&
      lastAgentMessage?.message?.includes("draft_emails")) ||
    lastAgentMessage?.agentIdName == "jd_details_agent" ||
    isSowCompleted;

  // Show intake form for Interview Questions agents when no chat history
  const isInterviewQuestionsAgent = agentId === "60" || agentId === "41";
  const showIntakeForm = isInterviewQuestionsAgent && !hasMessages;

  // Show Learning Path Button for Agent 57 when no messages
  const showLearningPathButton = agentId === "57" && !hasMessages;

  // Hide chat input for RFQ Builder (agent 45) when there are messages
  const hideInputForRfqBuilder = agentId === "45" && hasMessages;

  return (
    <>
      <div
        className={styles.chatWindowContainer}
        // style={chatWindowColor ? { background: chatWindowColor } : {}}
      >
        {showFullScreenSpinner ? (
          <div className={styles.chatWindow}>
            <LoadingSpinner size="lg" label="Loading session..." fullscreen />
          </div>
        ) : showIntakeForm ? (
          <div className={styles.chatWindow}>
            <IntakeAgentForm parsedContent={undefined} />
          </div>
        ) : hasMessages ? (
          <>
            <MessageBubble
              currentChat={currentChat}
              agentAvatar="/images/accellorAILabs_logo_s.svg"
              showThinkingBubble={loading}
              inputRef={inputRef}
            />
          </>
        ) : (
          <>
            <div className={styles.chatHeader}>
              <ContentCard
                title={agentTitle}
                imageUrl="../../assets/images/accellorAILabs_logo_s.svg"
                subTitle={agentSubTitle}
              />
            </div>
            {/* Prompt Template Modal */}
            <div>
              
              <PredefinedPrompts
                isSmallChatWindow={false}
                prompts={defaultPrompts}
                onPromptClick={onPromptClick}
              />

              {/* Learning Path Button - Show only for agent 57 when no messages exist */}
              {showLearningPathButton && (
                <LearningPathButton />
              )}
            </div>
          </>
        )}
      </div>
      {!showIntakeForm && !showLearningPathButton && !hideInputForRfqBuilder && (
        <div className={styles.inputContainer}>
          <MessageInput
            ref={inputRef}
            agentId={agentId}
            currentAgent={agent}
            value={value}
            setValue={setValue}
            showGenerateImageToggle={agentId === "11" || agentId === "106"}
            disableImageToggle={hasAgentResponse && agentId !== "106"}
            showUploadProfilesToggle={agentId === "18"}
            disableUploadProfilesToggle={hasAgentResponse}
            disabled={isWorkflowEnded}
          />
        </div>
      )}
    </>
  );
};

export default ChatWindow;
